import { Box, Checkbox, checkboxClasses, FormControlLabel, Stack, Typography } from '@mui/material'
import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

export interface WallboxLegendEntry {
  id: string
  deviceName: string
  shade: string
}

interface ChargingSessionsChartLegendProps {
  gridWallboxes: WallboxLegendEntry[]
  solarWallboxes: WallboxLegendEntry[]
  displayEnergyAll: boolean
  hiddenGridWallboxIds: string[]
  hiddenSolarWallboxIds: string[]
  toggleHiddenWallbox: (wallboxType: 'grid' | 'solar', wallboxId: string) => void
  /** use styles for small display */
  smallDisplay: boolean
}

/**
 * Chart legend component for emobility charging sessions chart.
 * It holds total energy, grid energy and solar energy legend
 * as well as the names and shades of wallboxes
 */
export const ChargingSessionsChartLegend: React.FC<ChargingSessionsChartLegendProps> = ({
  gridWallboxes,
  solarWallboxes,
  displayEnergyAll,
  hiddenGridWallboxIds,
  hiddenSolarWallboxIds,
  toggleHiddenWallbox,
  smallDisplay,
}) => {
  const { t } = useCustomTranslation()

  return (
    <Box>
      <Typography component={'h5'} fontSize={17} fontWeight={'bold'} pl={1}>
        {t('emobility.chargingSession.legendTitle')}
      </Typography>

      <Stack
        direction={smallDisplay ? 'row' : 'column'}
        pt={2}
        px={2}
        gap={0.5}
        sx={smallDisplay ? { flexWrap: 'wrap' } : { overflowY: 'auto', maxHeight: '350px' }}
      >
        {gridWallboxes.map(wallbox => {
          const isHidden = hiddenGridWallboxIds.includes(wallbox.id)
          return (
            <FormControlLabel
              key={wallbox.id}
              control={
                <Checkbox
                  checked={!isHidden}
                  onChange={() => toggleHiddenWallbox('grid', wallbox.id)}
                  sx={{
                    p: 0,
                    mr: 1.5,
                    [`&, &.${checkboxClasses.checked}`]: {
                      color: wallbox.shade,
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: wallbox.shade, overflowWrap: 'anywhere' }}>
                  {t('emobility.chargingSession.wallboxStartGrid')} {wallbox.deviceName}
                </Typography>
              }
            />
          )
        })}

        {displayEnergyAll &&
          solarWallboxes.map(wallbox => {
            const isHidden = hiddenSolarWallboxIds.includes(wallbox.id)
            return (
              <FormControlLabel
                key={wallbox.id}
                control={
                  <Checkbox
                    checked={!isHidden}
                    onChange={() => toggleHiddenWallbox('solar', wallbox.id)}
                    sx={{
                      p: 0,
                      mr: 1.5,
                      [`&, &.${checkboxClasses.checked}`]: {
                        color: wallbox.shade,
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: wallbox.shade, overflowWrap: 'anywhere' }}>
                    {t('emobility.chargingSession.wallboxStartSolar')} {wallbox.deviceName}
                  </Typography>
                }
              />
            )
          })}
      </Stack>
    </Box>
  )
}
